import { normalizeLamellaSequence, normalizeShoePositions } from '../../CustomTableTabs/CadworkUtils';

export const DEFAULT_HYPERBEAM_MM_TO_SCENE = 0.001;
export const DEFAULT_HYPERBEAM_PREFIX_BIAS = 0.7;
export const DEFAULT_HYPERBEAM_SHOE_SPACING = 400;
export const DEFAULT_HYPERBEAM_STACK_SIDE = -1;
export const DEFAULT_HYPERBEAM_HELPER_FACE_MARGIN = 18;

function toFiniteNumber(value) {
  const parsed = Number.parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function lerp(start, end, ratio) {
  return start + (end - start) * ratio;
}

function createPoint(x = 0, z = 0) {
  return { x, z };
}

function subtractPoint(left, right) {
  return createPoint(left.x - right.x, left.z - right.z);
}

function scalePoint(point, factor) {
  return createPoint(point.x * factor, point.z * factor);
}

function pointDistance(left, right) {
  return Math.hypot(right.x - left.x, right.z - left.z);
}

function normalizePoint(point, fallback = createPoint(1, 0)) {
  const length = Math.hypot(point.x, point.z);
  if (!Number.isFinite(length) || length <= 0) {
    return fallback;
  }
  return createPoint(point.x / length, point.z / length);
}

function tangentFromAngle(angleDeg) {
  const radians = ((Number(angleDeg) || 0) * Math.PI) / 180;
  return normalizePoint(createPoint(Math.cos(radians), Math.sin(radians)));
}

function shoeDirectionFromAngle(angleDeg) {
  const radians = ((Number(angleDeg) || 0) * Math.PI) / 180;
  return normalizePoint(createPoint(Math.sin(radians), -Math.cos(radians)), createPoint(0, -1));
}

function leftNormalFromTangent(tangent) {
  return normalizePoint(createPoint(-tangent.z, tangent.x), createPoint(0, 1));
}

function sampleHermiteSegment(pointA, pointB, tangentA, tangentB, segments) {
  const points = [];
  const safeSegments = Math.max(1, segments);

  for (let index = 0; index <= safeSegments; index += 1) {
    const t = index / safeSegments;
    const t2 = t * t;
    const t3 = t2 * t;

    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + t;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;

    points.push(createPoint(h00 * pointA.x + h10 * tangentA.x + h01 * pointB.x + h11 * tangentB.x, h00 * pointA.z + h10 * tangentA.z + h01 * pointB.z + h11 * tangentB.z));
  }

  return points;
}

function withArcLength(points) {
  let runningLength = 0;

  return points.map((point, index) => {
    if (index > 0) {
      runningLength += pointDistance(points[index - 1], point);
    }

    return {
      ...point,
      s: runningLength
    };
  });
}

function interpolatePointByRatio(left, right, ratio) {
  return {
    x: lerp(left.x, right.x, ratio),
    z: lerp(left.z, right.z, ratio),
    s: lerp(left.s ?? 0, right.s ?? 0, ratio)
  };
}

function findSegmentRatioByTarget(left, right, target, axis = 's') {
  const delta = right[axis] - left[axis];
  if (!Number.isFinite(delta) || Math.abs(delta) < 1e-6) {
    return 0;
  }
  return clamp((target - left[axis]) / delta, 0, 1);
}

function extendPointByTangent(originPoint, tangent, distanceMm) {
  return {
    x: originPoint.x + tangent.x * distanceMm,
    z: originPoint.z + tangent.z * distanceMm
  };
}

function computePolylineTangent(points, index, fallback = createPoint(1, 0)) {
  if (!Array.isArray(points) || points.length < 2) {
    return fallback;
  }

  if (index <= 0) {
    return normalizePoint(subtractPoint(points[1], points[0]), fallback);
  }

  if (index >= points.length - 1) {
    return normalizePoint(subtractPoint(points[points.length - 1], points[points.length - 2]), fallback);
  }

  return normalizePoint(subtractPoint(points[index + 1], points[index - 1]), fallback);
}

function findPointAtArcLength(points, targetArcLength) {
  if (!Array.isArray(points) || points.length === 0) {
    return null;
  }

  if (points.length === 1) {
    return { ...points[0] };
  }

  if (targetArcLength <= 0) {
    const tangent = computePolylineTangent(points, 0);
    return {
      x: points[0].x + tangent.x * targetArcLength,
      z: points[0].z + tangent.z * targetArcLength,
      s: targetArcLength
    };
  }

  const totalLength = points[points.length - 1].s;
  if (targetArcLength >= totalLength) {
    const tangent = computePolylineTangent(points, points.length - 1);
    const overflow = targetArcLength - totalLength;
    return {
      x: points[points.length - 1].x + tangent.x * overflow,
      z: points[points.length - 1].z + tangent.z * overflow,
      s: targetArcLength
    };
  }

  for (let index = 1; index < points.length; index += 1) {
    if (targetArcLength <= points[index].s) {
      return interpolatePointByRatio(points[index - 1], points[index], findSegmentRatioByTarget(points[index - 1], points[index], targetArcLength, 's'));
    }
  }

  return { ...points[points.length - 1] };
}

function findTangentAtArcLength(points, targetArcLength, fallback = createPoint(1, 0)) {
  if (!Array.isArray(points) || points.length < 2) {
    return fallback;
  }

  if (targetArcLength <= 0) {
    return computePolylineTangent(points, 0, fallback);
  }

  const totalLength = points[points.length - 1].s;
  if (targetArcLength >= totalLength) {
    return computePolylineTangent(points, points.length - 1, fallback);
  }

  for (let index = 1; index < points.length; index += 1) {
    if (targetArcLength <= points[index].s) {
      return normalizePoint(subtractPoint(points[index], points[index - 1]), fallback);
    }
  }

  return computePolylineTangent(points, points.length - 1, fallback);
}

function buildLinearExtensionSamples(originPoint, tangent, extensionMm, sampleSpacingMm, direction = 1, includeOrigin = true) {
  if (!originPoint || !Number.isFinite(extensionMm) || extensionMm <= 0) {
    return includeOrigin && originPoint ? [{ x: originPoint.x, z: originPoint.z }] : [];
  }

  const safeSpacing = Math.max(20, Number(sampleSpacingMm) || 40);
  const safeDirection = direction === -1 ? -1 : 1;
  const segmentCount = clamp(Math.ceil(extensionMm / safeSpacing), 1, 240);
  const samples = [];

  for (let index = 0; index <= segmentCount; index += 1) {
    if (!includeOrigin && index === 0) {
      continue;
    }

    const distanceMm = (index / segmentCount) * extensionMm * safeDirection;
    samples.push({
      x: originPoint.x + tangent.x * distanceMm,
      z: originPoint.z + tangent.z * distanceMm
    });
  }

  return samples;
}

function buildExtendedGuideSamples(coreCurve, extensionBeforeMm, extensionAfterMm, sampleSpacingMm) {
  if (!Array.isArray(coreCurve) || coreCurve.length === 0) {
    return [];
  }

  const safeSpacing = Math.max(20, Number(sampleSpacingMm) || 40);
  const startPoint = coreCurve[0];
  const endPoint = coreCurve[coreCurve.length - 1];
  const startTangent = computePolylineTangent(coreCurve, 0);
  const endTangent = computePolylineTangent(coreCurve, coreCurve.length - 1);

  const beforePoints = buildLinearExtensionSamples(startPoint, startTangent, extensionBeforeMm, safeSpacing, -1, true).reverse();
  const middlePoints = coreCurve.map((point, index) => (index === 0 && beforePoints.length ? null : { x: point.x, z: point.z })).filter(Boolean);
  const afterPoints = buildLinearExtensionSamples(endPoint, endTangent, extensionAfterMm, safeSpacing, 1, false);

  return withArcLength([...beforePoints, ...middlePoints, ...afterPoints]);
}

function resolveLamellaBeamSpan(entry) {
  const lengthMm = toFiniteNumber(entry?.length);
  const startPositionMm = toFiniteNumber(entry?.startPosition);
  const endPositionMm = toFiniteNumber(entry?.endPosition);

  if (Number.isFinite(startPositionMm) && Number.isFinite(endPositionMm) && endPositionMm > startPositionMm) {
    return {
      startMm: startPositionMm,
      endMm: endPositionMm
    };
  }

  if (Number.isFinite(startPositionMm) && Number.isFinite(lengthMm)) {
    return {
      startMm: startPositionMm,
      endMm: startPositionMm + lengthMm
    };
  }

  if (Number.isFinite(endPositionMm) && Number.isFinite(lengthMm)) {
    return {
      startMm: Math.max(0, endPositionMm - lengthMm),
      endMm: endPositionMm
    };
  }

  return {
    startMm: 0,
    endMm: Number.isFinite(lengthMm) ? lengthMm : 0
  };
}

function resolveGlobalBeamLength(sequence, referenceFromOriginMm, referenceFromExtremityMm) {
  const candidates = sequence.flatMap((entry) => {
    const span = resolveLamellaBeamSpan(entry);
    return [span.startMm, span.endMm, toFiniteNumber(entry?.length)];
  });

  if (Number.isFinite(referenceFromOriginMm) && Number.isFinite(referenceFromExtremityMm)) {
    candidates.push(referenceFromOriginMm + referenceFromExtremityMm);
  }

  const finiteCandidates = candidates.filter((value) => Number.isFinite(value) && value >= 0);
  return finiteCandidates.length ? Math.max(...finiteCandidates) : 0;
}

function buildGuideCurveSamples(shoePositions, options = {}) {
  const shoeSpacingMm = Number(options.shoeSpacingMm) || DEFAULT_HYPERBEAM_SHOE_SPACING;
  const samplesPerSegment = Math.max(12, Number(options.samplesPerSegment) || 28);
  const extensionBeforeMm = Math.max(0, Number(options.extensionBeforeMm) || 0);
  const extensionAfterMm = Math.max(0, Number(options.extensionAfterMm) || 0);

  const shoePoints = shoePositions.map((shoe, index) => {
    const shoeIndex = shoe.index ?? index + 1;
    const baseX = (shoeIndex - 1) * shoeSpacingMm;
    return {
      index: shoeIndex,
      basePoint: createPoint(baseX, 0),
      helperDirection: shoeDirectionFromAngle(shoe.angle),
      point: createPoint(baseX, -shoe.position),
      tangent: tangentFromAngle(shoe.angle)
    };
  });

  const controlPoints = shoePoints.map((shoePoint, index) => {
    const prevPoint = shoePoints[index - 1]?.point;
    const nextPoint = shoePoints[index + 1]?.point;
    const prevDistance = prevPoint ? pointDistance(prevPoint, shoePoint.point) : null;
    const nextDistance = nextPoint ? pointDistance(shoePoint.point, nextPoint) : null;
    const localDistance = [prevDistance, nextDistance].filter((value) => Number.isFinite(value) && value > 0);
    const tangentScale = localDistance.length ? localDistance.reduce((sum, value) => sum + value, 0) / localDistance.length : shoeSpacingMm;
    const tangentBasis =
      prevPoint && nextPoint ? subtractPoint(nextPoint, prevPoint) : nextPoint ? subtractPoint(nextPoint, shoePoint.point) : prevPoint ? subtractPoint(shoePoint.point, prevPoint) : createPoint(1, 0);

    return {
      point: shoePoint.point,
      tangent: scalePoint(normalizePoint(tangentBasis, createPoint(1, 0)), tangentScale)
    };
  });

  if (controlPoints.length === 0) {
    return {
      shoePoints: [],
      samples: []
    };
  }

  if (controlPoints.length === 1) {
    const singlePoint = controlPoints[0].point;
    return {
      shoePoints: shoePoints.map((shoePoint) => ({
        ...shoePoint,
        baseX: shoePoint.basePoint.x,
        baseZ: shoePoint.basePoint.z,
        x: shoePoint.point.x,
        z: shoePoint.point.z
      })),
      samples: withArcLength([singlePoint])
    };
  }

  const extendedControlPoints = [...controlPoints];

  if (extensionBeforeMm > 0) {
    const startPoint = controlPoints[0].point;
    const startTangent = normalizePoint(controlPoints[0].tangent, createPoint(1, 0));
    extendedControlPoints.unshift({
      point: extendPointByTangent(startPoint, scalePoint(startTangent, -1), extensionBeforeMm),
      tangent: scalePoint(startTangent, extensionBeforeMm)
    });
  }

  if (extensionAfterMm > 0) {
    const endPoint = controlPoints[controlPoints.length - 1].point;
    const endTangent = normalizePoint(controlPoints[controlPoints.length - 1].tangent, createPoint(1, 0));
    extendedControlPoints.push({
      point: extendPointByTangent(endPoint, endTangent, extensionAfterMm),
      tangent: scalePoint(endTangent, extensionAfterMm)
    });
  }

  const sampledPoints = [];
  for (let index = 0; index < extendedControlPoints.length - 1; index += 1) {
    const segmentPoints = sampleHermiteSegment(
      extendedControlPoints[index].point,
      extendedControlPoints[index + 1].point,
      extendedControlPoints[index].tangent,
      extendedControlPoints[index + 1].tangent,
      samplesPerSegment
    );
    if (index > 0) {
      segmentPoints.shift();
    }
    sampledPoints.push(...segmentPoints);
  }

  return {
    shoePoints: shoePoints.map((shoePoint) => ({
      ...shoePoint,
      baseX: shoePoint.basePoint.x,
      baseZ: shoePoint.basePoint.z,
      x: shoePoint.point.x,
      z: shoePoint.point.z
    })),
    samples: withArcLength(sampledPoints)
  };
}

function resolveBeamColor(quality, beamOptions = {}) {
  const beamColors = Array.isArray(beamOptions.beamColors) && beamOptions.beamColors.length ? beamOptions.beamColors : ['#c48a57'];
  const beamBuildText = Array.isArray(beamOptions.beamBuildText) ? beamOptions.beamBuildText : [];
  const normalizedQuality = String(quality || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

  const colorIndex = beamBuildText.findIndex((entry) => {
    const normalizedEntry = String(entry || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
    return normalizedEntry === normalizedQuality;
  });

  return beamColors[colorIndex >= 0 ? colorIndex % beamColors.length : 0] || '#c48a57';
}

function computeBounds(lamellas, shoeMarkers, referenceMarker, lamellaWidthMm) {
  const allPoints = [];
  lamellas.forEach((lamella) => {
    lamella.pointsMm.forEach((point) => {
      allPoints.push(point);
    });
  });
  shoeMarkers.forEach((shoe) => {
    allPoints.push({ x: shoe.x, y: 0, z: shoe.z });
    if (Number.isFinite(shoe.endX) && Number.isFinite(shoe.endZ)) {
      allPoints.push({ x: shoe.endX, y: 0, z: shoe.endZ });
    }
  });
  if (referenceMarker) {
    allPoints.push({ x: referenceMarker.x, y: 0, z: referenceMarker.z });
  }

  if (!allPoints.length) {
    return {
      minX: 0,
      maxX: 0,
      minY: -lamellaWidthMm / 2,
      maxY: lamellaWidthMm / 2,
      minZ: 0,
      maxZ: 0,
      centerX: 0,
      centerZ: 0
    };
  }

  const minX = Math.min(...allPoints.map((point) => point.x));
  const maxX = Math.max(...allPoints.map((point) => point.x));
  const minZ = Math.min(...allPoints.map((point) => point.z));
  const maxZ = Math.max(...allPoints.map((point) => point.z));

  return {
    minX,
    maxX,
    minY: -lamellaWidthMm / 2,
    maxY: lamellaWidthMm / 2,
    minZ,
    maxZ,
    centerX: (minX + maxX) / 2,
    centerZ: (minZ + maxZ) / 2
  };
}

function tangentSlope(tangent) {
  if (!tangent || !Number.isFinite(tangent.x) || Math.abs(tangent.x) < 1e-6) {
    return tangent?.z >= 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  }

  return tangent.z / tangent.x;
}

function tangentAngleDeg(tangent) {
  return (Math.atan2(tangent?.z || 0, tangent?.x || 0) * 180) / Math.PI;
}

function findZeroTangentArcLength(guideCurve) {
  if (!Array.isArray(guideCurve) || guideCurve.length < 2) {
    return null;
  }

  let bestMatch = null;

  for (let index = 0; index < guideCurve.length; index += 1) {
    const sample = guideCurve[index];
    const tangent = findTangentAtArcLength(guideCurve, sample.s);
    const absZ = Math.abs(tangent.z);

    if (!bestMatch || absZ < bestMatch.absZ) {
      bestMatch = {
        s: sample.s,
        tangent,
        absZ,
        exactZero: absZ < 1e-6
      };
    }

    if (index === 0) {
      continue;
    }

    const previous = guideCurve[index - 1];
    const previousTangent = findTangentAtArcLength(guideCurve, previous.s);

    if (previousTangent.z * tangent.z < 0) {
      const ratio = clamp(Math.abs(previousTangent.z) / (Math.abs(previousTangent.z) + Math.abs(tangent.z)), 0, 1);
      const s = lerp(previous.s, sample.s, ratio);

      return {
        s,
        tangent: findTangentAtArcLength(guideCurve, s),
        absZ: 0,
        exactZero: true
      };
    }
  }

  return bestMatch;
}

function findClosestShoeMarker(point, shoeMarkers) {
  if (!point || !Array.isArray(shoeMarkers) || !shoeMarkers.length) {
    return null;
  }

  return shoeMarkers.reduce((closest, shoeMarker) => {
    const distanceMm = pointDistance(point, shoeMarker);
    if (!closest || distanceMm < closest.distanceMm) {
      return {
        index: shoeMarker.index,
        x: shoeMarker.x,
        z: shoeMarker.z,
        angle: shoeMarker.angle,
        distanceMm
      };
    }

    return closest;
  }, null);
}

export function buildHyperBeamPreviewModel(rawHyperBeam, beamOptions = {}, options = {}) {
  const hyperBeam = rawHyperBeam || {};
  const shoeSpacingMm = Number(hyperBeam.shoeSpacingMm) || DEFAULT_HYPERBEAM_SHOE_SPACING;
  const shoePositions = normalizeShoePositions(hyperBeam.shoePositions, hyperBeam.fallbackShoePositions);
  const sequence = normalizeLamellaSequence(hyperBeam.sequence);
  const lamellaWidthMm = toFiniteNumber(hyperBeam.lamellaWidthMm);
  const lamellaThicknessMm = toFiniteNumber(hyperBeam.lamellaThicknessMm);
  const referenceFromOriginMm = toFiniteNumber(hyperBeam.referenceFromOriginMm);
  const referenceFromExtremityMm = toFiniteNumber(hyperBeam.referenceFromExtremityMm);
  const requestedStackSide = Number(options.stackSide);
  const stackSide = requestedStackSide === -1 || requestedStackSide === 1 ? requestedStackSide : DEFAULT_HYPERBEAM_STACK_SIDE;
  const helperFaceMarginMm = Math.max(8, Number(options.helperFaceMarginMm) || DEFAULT_HYPERBEAM_HELPER_FACE_MARGIN);

  const warnings = [];
  const errors = [];

  if (shoePositions.length < 2) {
    errors.push('At least two shoe positions are required for HyperBeamModel.');
  }
  if (!sequence.length) {
    errors.push('Lamella sequence is required for HyperBeamModel.');
  }
  if (!Number.isFinite(lamellaWidthMm) || lamellaWidthMm <= 0) {
    errors.push('Lamella width must be a positive number.');
  }
  if (!Number.isFinite(lamellaThicknessMm) || lamellaThicknessMm <= 0) {
    errors.push('Lamella thickness must be a positive number.');
  }

  if (errors.length) {
    return {
      status: 'error',
      warnings,
      errors,
      shoePositions,
      sequence,
      lamellas: [],
      shoeMarkers: [],
      guideCurve: [],
      coreGuideCurve: [],
      referenceMarker: null,
      bounds: computeBounds([], [], null, lamellaWidthMm || 0),
      sceneOffsetMm: { x: 0, y: 0, z: 0 }
    };
  }

  if (!Number.isFinite(referenceFromOriginMm) || !Number.isFinite(referenceFromExtremityMm)) {
    warnings.push('LSL reference data is missing; HyperBeamModel uses fallback wrap distribution.');
  }

  const referenceSum = Number.isFinite(referenceFromOriginMm) && Number.isFinite(referenceFromExtremityMm) ? referenceFromOriginMm + referenceFromExtremityMm : 0;
  const prefixBias = referenceSum > 0 ? clamp((referenceFromExtremityMm || 0) / referenceSum, 0.55, 0.85) : DEFAULT_HYPERBEAM_PREFIX_BIAS;

  const coreGuide = buildGuideCurveSamples(shoePositions, {
    shoeSpacingMm,
    samplesPerSegment: options.samplesPerSegment
  });
  const coreCurve = coreGuide.samples;
  const coreCurveLengthMm = coreCurve[coreCurve.length - 1]?.s || 0;
  const globalBeamLengthMm = resolveGlobalBeamLength(sequence, referenceFromOriginMm, referenceFromExtremityMm);
  const totalOverflowMm = Math.max(0, globalBeamLengthMm - coreCurveLengthMm);
  const extensionBeforeMm = totalOverflowMm * prefixBias;
  const extensionAfterMm = totalOverflowMm - extensionBeforeMm;
  const guideCurve = buildExtendedGuideSamples(coreCurve, extensionBeforeMm, extensionAfterMm, Math.max(28, shoeSpacingMm / 12));
  const referenceMarker = Number.isFinite(referenceFromOriginMm)
    ? findPointAtArcLength(guideCurve, referenceFromOriginMm)
    : {
        ...guideCurve[0]
      };

  const lamellas = sequence.map((entry, index) => {
    const { startMm: beamStartMm, endMm: beamEndMm } = resolveLamellaBeamSpan(entry);
    const startArcLengthMm = beamStartMm;
    const endArcLengthMm = beamEndMm;
    const beamSpanMm = Math.max(0, beamEndMm - beamStartMm);
    const offsetMm = (index + 0.5) * lamellaThicknessMm;
    const sampleCount = clamp(Math.ceil(beamSpanMm / Math.max(36, shoeSpacingMm / 10)) + 1, 24, 320);
    const pointsMm = [];

    for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
      const ratio = sampleCount === 1 ? 0 : sampleIndex / (sampleCount - 1);
      const curveArcLength = lerp(startArcLengthMm, endArcLengthMm, ratio);
      const guidePoint = findPointAtArcLength(guideCurve, curveArcLength);
      const tangent = findTangentAtArcLength(guideCurve, curveArcLength);
      const normal = leftNormalFromTangent(tangent);
      pointsMm.push({
        x: guidePoint.x + normal.x * offsetMm * stackSide,
        y: 0,
        z: guidePoint.z + normal.z * offsetMm * stackSide
      });
    }

    return {
      id: entry.id,
      key: `hyperbeam-lamella-${entry.id}-${index}`,
      quality: entry.quality || 'default',
      lengthMm: entry.length,
      widthMm: lamellaWidthMm,
      thicknessMm: lamellaThicknessMm,
      offsetMm,
      beamStartMm,
      beamEndMm,
      startArcLengthMm,
      endArcLengthMm,
      color: resolveBeamColor(entry.quality || 'default', beamOptions),
      pointsMm
    };
  });

  const shoeMarkerLengthMm = Math.max(lamellaThicknessMm * 4, 120);
  const helperYOffsetMm = lamellaWidthMm / 2 + helperFaceMarginMm;
  const shoeMarkers = coreGuide.shoePoints.map((shoePoint) => {
    const shoe = shoePositions.find((entry) => entry.index === shoePoint.index);
    const helperDirection = shoeDirectionFromAngle(shoe?.angle);

    return {
      index: shoePoint.index,
      baseX: shoePoint.baseX,
      baseZ: shoePoint.baseZ,
      x: shoePoint.x,
      z: shoePoint.z,
      angle: shoe?.angle ?? 0,
      helperY: helperYOffsetMm,
      endX: shoePoint.x + helperDirection.x * shoeMarkerLengthMm,
      endZ: shoePoint.z + helperDirection.z * shoeMarkerLengthMm
    };
  });

  const bounds = computeBounds(lamellas, shoeMarkers, referenceMarker, lamellaWidthMm);

  return {
    status: 'ready',
    warnings,
    errors,
    prefixBias,
    lamellaWidthMm,
    lamellaThicknessMm,
    coreCurveLengthMm,
    globalBeamLengthMm,
    extensionBeforeMm,
    extensionAfterMm,
    shoeSpacingMm,
    stackSide,
    helperYOffsetMm,
    guideCurve,
    coreGuideCurve: coreCurve,
    shoeMarkers,
    referenceMarker,
    lamellas,
    bounds,
    sceneOffsetMm: {
      x: -bounds.centerX,
      y: 0,
      z: -bounds.centerZ
    }
  };
}

export function findHyperBeamZeroTangentPoint(rawHyperBeam, options = {}) {
  const preview = buildHyperBeamPreviewModel(rawHyperBeam, {}, options);

  if (preview.status !== 'ready') {
    return {
      status: 'error',
      errors: preview.errors || ['Unable to build HyperBeam guide curve.'],
      point: null,
      tangent: null,
      nearestShoe: null,
      guideCurve: preview.guideCurve || [],
      shoeMarkers: preview.shoeMarkers || []
    };
  }

  const zeroTangent = findZeroTangentArcLength(preview.guideCurve);
  const point = zeroTangent ? findPointAtArcLength(preview.guideCurve, zeroTangent.s) : null;
  const tangent = zeroTangent ? findTangentAtArcLength(preview.guideCurve, zeroTangent.s) : null;
  const nearestShoe = findClosestShoeMarker(point, preview.shoeMarkers);

  if (!point || !tangent) {
    return {
      status: 'error',
      errors: ['Unable to resolve a tangent-zero point on the HyperBeam guide curve.'],
      point: null,
      tangent: null,
      nearestShoe: null,
      guideCurve: preview.guideCurve,
      shoeMarkers: preview.shoeMarkers
    };
  }

  return {
    status: 'ready',
    errors: [],
    point: {
      x: point.x,
      z: point.z,
      s: point.s
    },
    tangent: {
      x: tangent.x,
      z: tangent.z,
      slope: tangentSlope(tangent),
      angleDeg: tangentAngleDeg(tangent),
      absZ: Math.abs(tangent.z),
      exactZero: Boolean(zeroTangent?.exactZero)
    },
    nearestShoe,
    guideCurve: preview.guideCurve,
    shoeMarkers: preview.shoeMarkers
  };
}
